// src/components/OS/ModalNotasFiscais.jsx
import React from "react";
import {
  FaFileInvoiceDollar,
  FaBarcode,
  FaCalendar,
  FaTimes,
  FaSync,
  FaRegCopy,
  FaRegStickyNote,
  FaExclamationCircle,
  FaLink,
  FaExternalLinkAlt,
} from "react-icons/fa";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";

/* ======================= Helpers ======================= */
const ensureFocusAbsolute = (maybePath) =>
  maybePath && !/^https?:\/\//i.test(maybePath)
    ? `https://api.focusnfe.com.br${maybePath}`
    : maybePath || "";

const looksLikeFocus = (u = "") =>
  /focusnfe\.com\.br/i.test(u) || /^\//.test(u);

function copyToClipboard(text) {
  if (!text) return;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}
function fallbackCopy(text) {
  try {
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    document.execCommand("copy");
    document.body.removeChild(area);
  } catch (_) {}
}

function mapStatus(statusRaw) {
  const s = (statusRaw || "").toString().toLowerCase();
  const emitidaSet = new Set(["emitida", "aprovada", "autorizada", "autorizado"]);
  if (emitidaSet.has(s)) return { label: "Emitida", cls: "bg-green-100 text-green-700" };
  if (s.includes("process") || s.includes("aguard") || s.includes("pend"))
    return { label: "Aguardando autorização", cls: "bg-yellow-100 text-yellow-700" };
  if (s.includes("cancelad")) return { label: "Cancelada", cls: "bg-gray-200 text-gray-800" };
  if (s.includes("deneg")) return { label: "Denegada", cls: "bg-red-100 text-red-700" };
  if (s.includes("erro") || s.includes("rejei"))
    return { label: "Erro na emissão", cls: "bg-red-200 text-red-800" };
  if (!statusRaw) return { label: "Desconhecido", cls: "bg-gray-300 text-gray-800" };
  return { label: statusRaw, cls: "bg-gray-300 text-gray-800" };
}

const isPending = (statusRaw) => {
  const s = (statusRaw || "").toString().toLowerCase();
  return s.includes("process") || s.includes("aguard") || s.includes("pend");
};

const StatusBadge = ({ status }) => {
  const { label, cls } = mapStatus(status);
  return (
    <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${cls}`}>
      {label}
    </span>
  );
};

// melhor mensagem de erro disponível
const getErroNota = (nota = {}) =>
  nota.ultimoErro ||
  nota.mensagem_sefaz ||
  nota.mensagem ||
  nota.erro ||
  nota?.detalhes?.mensagem_sefaz ||
  nota?.detalhes?.mensagem ||
  "";

// status efetivo: erro > qualquer outro
const statusEfetivo = (nota = {}) => (getErroNota(nota) ? "erro" : (nota.status || ""));

/* ======================= Componente ======================= */
const ModalNotasFiscais = ({
  open,
  onClose,
  notas = [],
  onCancelarNota = () => {},
  loadingCancel = {},
  onAtualizarNota = () => {},
  loadingRefresh = {},
  os,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl max-w-2xl w-full p-10 relative animate-modalSlideIn overflow-y-auto max-h-[92vh] border border-gray-100 dark:border-gray-800">
        {/* Header */}
        <button
          className="absolute right-8 top-7 text-gray-500 hover:text-[#004072] text-base font-medium dark:text-gray-300 dark:hover:text-[#60a5fa]"
          onClick={onClose}
        >
          Fechar
        </button>

        <h2 className="text-2xl font-bold mb-2 text-[#004072] dark:text-white flex items-center gap-3">
          <FaFileInvoiceDollar className="text-2xl" /> Detalhes Fiscais
        </h2>

        {os && (
          <div className="mb-6 text-sm text-gray-700 dark:text-gray-300">
            <div className="flex flex-wrap gap-2">
              <span className="font-semibold">OS #{os.numeroOS ?? "..."}</span>
              <span>•</span>
              <span>{os.clienteNome || "-"}</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {os.veiculoDescricao || "-"}
            </div>
          </div>
        )}

        {!notas?.length ? (
          <div className="flex items-center gap-3 text-gray-500 dark:text-gray-300">
            <FaExclamationCircle className="text-xl" />
            Nenhuma nota fiscal foi gerada para esta OS.
          </div>
        ) : (
          <div className="grid gap-6">
            {notas.map((nota, idx) => {
              const isNFe = nota.tipo === "nfe";
              const isNFSe = nota.tipo === "nfse";

              // Chaves/refs
              const chaveRaw =
                nota.chave || nota.nfeChave || (os?.nfeChave ?? "") || "";
              const chave = (chaveRaw || "").replace(/^NFe/, "");
              const nfseRef =
                nota.nfseRef || nota.ref || (os?.nfseRef ?? "") || "";
              const nfeRef =
                nota.nfeRef || nota.ref || (os?.nfeRef ?? "") || "";

              // --------- NF-e: prioriza SEMPRE o backend (aceita chave OU ref) ---------
              const nfeId = encodeURIComponent(chave || nfeRef || "");
              const savedDanfe = ensureFocusAbsolute(nota.linkDanfe || nota.nfeDanfeUrl || "");
              const savedXml   = ensureFocusAbsolute(nota.linkXml   || nota.nfeXmlUrl   || "");

              const danfeHref =
                (nfeId ? `${BACKEND_URL}/api/focus/baixar-danfe/${nfeId}` : "") ||
                // fallback: se por algum motivo veio um link que NÃO é da Focus (ex.: CDN externo)
                (!looksLikeFocus(savedDanfe) ? savedDanfe : "");

              const xmlHref =
                (nfeId ? `${BACKEND_URL}/api/focus/baixar-xml/${nfeId}` : "") ||
                (!looksLikeFocus(savedXml) ? savedXml : "");

              // --------- NFS-e: usar viewer do backend quando o link é da Focus (ou quando só temos ref) ---------
              const savedNfseLink = ensureFocusAbsolute(nota.link || nota.nfseUrl || "");
              const nfseViewer = nfseRef
                ? `${BACKEND_URL}/api/focus/visualizar-nfse/${encodeURIComponent(nfseRef)}`
                : "";
              const linkNfse =
                (looksLikeFocus(savedNfseLink) ? nfseViewer : savedNfseLink) ||
                nfseViewer ||
                "";

              // Links extras NFS-e (quando a API devolve)
              const linkNfsePdf  = ensureFocusAbsolute(nota.linkPdf  || "");
              const linkNfseHtml = ensureFocusAbsolute(nota.linkHtml || "");

              // estados de loading por tipo e por chave/ref
              const idForState = chave || nfseRef || nfeRef || nota.chave || "";
              const loadingCancelThis =
                !!loadingCancel?.[nota.tipo] || (!!idForState && !!loadingCancel?.[idForState]);
              const loadingRefreshThis =
                !!loadingRefresh?.[nota.tipo] || (!!idForState && !!loadingRefresh?.[idForState]);

              const erroMsg = getErroNota(nota);
              const statusToShow = statusEfetivo(nota);
              const pending = isPending(statusToShow);

              return (
                <div
                  key={`${idx}-${nota.tipo}-${chave || nfseRef || "semref"}`}
                  className="rounded-2xl border p-6 shadow dark:border-gray-800 bg-[#f7faff] dark:bg-gray-800 space-y-2"
                >
                  {/* Título / status */}
                  <div className="flex items-center gap-2 mb-2">
                    <FaFileInvoiceDollar
                      className={`text-lg ${
                        isNFe
                          ? "text-green-700 dark:text-green-400"
                          : "text-blue-700 dark:text-blue-300"
                      }`}
                    />
                    <span className="font-bold text-base text-[#004072] dark:text-blue-200">
                      {isNFe ? "NF-e (Produtos)" : "NFS-e (Serviços)"}
                    </span>
                    <StatusBadge status={statusToShow} />
                    {pending && (
                      <span className="ml-1 text-xs text-blue-700 dark:text-blue-300 animate-pulse">
                        Aguardando...
                      </span>
                    )}
                  </div>

                  {/* Campos */}
                  <div className="grid md:grid-cols-2 gap-3">
                    {!!nota.numero && (
                      <div className="flex items-center gap-2 text-sm">
                        <FaBarcode /> <span className="font-medium">Número:</span> {nota.numero}
                      </div>
                    )}
                    {!!nota.serie && (
                      <div className="flex items-center gap-2 text-sm">
                        <FaRegStickyNote /> <span className="font-medium">Série:</span> {nota.serie}
                      </div>
                    )}
                    {!!nota.dataEmissao && (
                      <div className="flex items-center gap-2 text-sm">
                        <FaCalendar /> <span className="font-medium">Emissão:</span> {nota.dataEmissao}
                      </div>
                    )}
                    {(!!chave || !!nfseRef || !!nfeRef) && (
                      <div className="flex items-center gap-2 text-xs font-mono">
                        <FaBarcode /> <span className="font-medium">Chave/Ref:</span>
                        <span className="truncate" title={chave || nfseRef || nfeRef}>
                          {chave || nfseRef || nfeRef}
                        </span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(chave || nfseRef || nfeRef)}
                          className="ml-1 px-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                          title="Copiar chave/ref"
                        >
                          <FaRegCopy className="text-xs" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Links */}
                  <div className="flex flex-wrap gap-3 mt-2">
                    {isNFe && !!danfeHref && (
                      <>
                        <a
                          href={danfeHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-700 dark:text-green-400 underline font-medium text-sm flex items-center gap-1"
                          title="Ver DANFe (PDF)"
                        >
                          <FaLink /> Ver DANFe (PDF) <FaExternalLinkAlt className="text-[10px]" />
                        </a>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(danfeHref)}
                          className="text-xs px-2 py-1 rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Copiar link do DANFe"
                        >
                          <FaRegCopy />
                        </button>
                      </>
                    )}
                    {isNFe && !!xmlHref && (
                      <>
                        <a
                          href={xmlHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-700 dark:text-green-400 underline font-medium text-sm flex items-center gap-1"
                          title="Baixar XML"
                        >
                          <FaLink /> Baixar XML <FaExternalLinkAlt className="text-[10px]" />
                        </a>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(xmlHref)}
                          className="text-xs px-2 py-1 rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Copiar link do XML"
                        >
                          <FaRegCopy />
                        </button>
                      </>
                    )}

                    {isNFSe && !!linkNfse && (
                      <>
                        <a
                          href={linkNfse}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 dark:text-blue-300 underline font-medium text-sm flex items-center gap-1"
                          title="Ver NFS-e (abre em nova guia)"
                        >
                          <FaLink /> Ver NFS-e <FaExternalLinkAlt className="text-[10px]" />
                        </a>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(linkNfse)}
                          className="text-xs px-2 py-1 rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Copiar link da NFS-e"
                        >
                          <FaRegCopy />
                        </button>
                      </>
                    )}

                    {isNFSe && !!linkNfsePdf && (
                      <>
                        <a
                          href={linkNfsePdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 dark:text-blue-300 underline font-medium text-sm flex items-center gap-1"
                          title="Baixar PDF da NFS-e"
                        >
                          <FaLink /> PDF <FaExternalLinkAlt className="text-[10px]" />
                        </a>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(linkNfsePdf)}
                          className="text-xs px-2 py-1 rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Copiar link do PDF"
                        >
                          <FaRegCopy />
                        </button>
                      </>
                    )}

                    {isNFSe && !!linkNfseHtml && (
                      <>
                        <a
                          href={linkNfseHtml}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 dark:text-blue-300 underline font-medium text-sm flex items-center gap-1"
                          title="Abrir HTML da NFS-e"
                        >
                          <FaLink /> HTML <FaExternalLinkAlt className="text-[10px]" />
                        </a>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(linkNfseHtml)}
                          className="text-xs px-2 py-1 rounded border dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Copiar link do HTML"
                        >
                          <FaRegCopy />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Erro da Focus */}
                  {!!erroMsg && (
                    <div className="mt-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg p-2 flex items-start gap-2">
                      <FaExclamationCircle className="mt-[2px]" />
                      <div className="text-sm">
                        <div className="font-semibold">Detalhes do erro</div>
                        <div title={erroMsg}>
                          {erroMsg.length > 220 ? `${erroMsg.slice(0, 220)}…` : erroMsg}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ações */}
                  <div className="flex flex-wrap gap-3 mt-3">
                    {(pending ||
                      String(statusToShow).toLowerCase().includes("erro") ||
                      String(statusToShow).toLowerCase().includes("rejei")) && (
                      <button
                        onClick={() => onAtualizarNota(nota)}
                        className="px-4 py-2 rounded-xl bg-blue-700 text-white text-sm font-bold flex items-center gap-2 hover:bg-blue-800 transition disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={loadingRefreshThis}
                        title="Atualizar status da nota"
                      >
                        {loadingRefreshThis ? <FaSync className="animate-spin" /> : <FaSync />}
                        Atualizar status
                      </button>
                    )}

                    {nota.podeCancelar &&
                      !String(statusToShow).toLowerCase().includes("cancelad") && (
                        <button
                          onClick={() => onCancelarNota(nota)}
                          className="px-4 py-2 rounded-xl bg-red-700 text-white text-sm font-bold flex items-center gap-2 hover:bg-red-800 transition disabled:opacity-70 disabled:cursor-not-allowed"
                          disabled={loadingCancelThis}
                          title={`Cancelar ${isNFe ? "NF-e" : "NFS-e"}`}
                        >
                          {loadingCancelThis ? <FaSync className="animate-spin" /> : <FaTimes />}
                          Cancelar {isNFe ? "NF-e" : "NFS-e"}
                        </button>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97);}
            100% { opacity:1; transform: translateY(0) scale(1);}
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
};

export default ModalNotasFiscais;
